import {
    buildSetItemPriceDraft,
    buildSetItemQuantityDefaultDraft,
    knownPrice,
} from "./calculator-layer-inspector-drafts.js";
import { calculatorDataText as t } from "./calculator-data-i18n.js";

const U32_MAX = 4_294_967_295;
const NON_NEGATIVE_DECIMAL_INPUT = /^(?:\d+(?:\.\d+)?|\.\d+)$/u;

function escapeText(value) {
    return String(value ?? "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#39;");
}

function spriteIcon(name, className = "size-4") {
    return `<svg class="fishy-icon ${escapeText(className)}" viewBox="0 0 24 24" aria-hidden="true"><use width="100%" height="100%" href="/img/icons.svg#fishy-${escapeText(name)}"></use></svg>`;
}

function effectiveItemFact(projection, kind, itemId) {
    const trace = projection?.provenanceTraces?.find((candidate) => (
        candidate.factKey?.kind === kind
        && candidate.factKey.itemId === itemId
    )) ?? null;
    const candidate = trace?.candidates?.find((entry) => entry.status === "effective") ?? null;
    return Object.freeze({
        trace,
        candidate,
        operation: candidate?.operation ?? null,
        userOperationId: candidate?.layer?.kind === "user"
            && typeof candidate.operation?.operationId === "string"
            ? candidate.operation.operationId
            : null,
    });
}

export function createCalculatorCatalogItemFactsEditorState({
    projection,
    itemId,
    openItemId,
}) {
    const priceFact = effectiveItemFact(projection, "itemPrice", itemId);
    const quantityDefaultFact = effectiveItemFact(
        projection,
        "itemQuantityDefault",
        itemId,
    );
    const price = priceFact.operation?.price ?? Object.freeze({ state: "unknown" });
    const quantityDefault = quantityDefaultFact.operation?.quantity
        ?? Object.freeze({ state: "known", minimum: 1, maximum: 1 });
    return Object.freeze({
        itemId,
        open: itemId === openItemId,
        price,
        priceUserOperationId: priceFact.userOperationId,
        quantityDefault,
        quantityDefaultUserOperationId: quantityDefaultFact.userOperationId,
    });
}

function itemFactRevertButton(operationId, label) {
    return operationId
        ? `<button class="btn btn-ghost btn-xs" type="button" data-action="revert-user-operation" data-operation-id="${escapeText(operationId)}">${escapeText(label)}</button>`
        : "";
}

export function renderCalculatorCatalogItemFactsControls({
    state,
    itemLabel,
    modelLoading,
}) {
    const panelId = `calculator-catalog-item-facts-${state.itemId}`;
    const toggleLabel = t(
        state.open
            ? "calculator.data.action.close_item_facts_for"
            : "calculator.data.action.edit_item_facts_for",
        state.open ? "Close facts for { $item }" : "Edit facts for { $item }",
        { item: itemLabel },
    );
    const toggleIcon = state.open ? "x-circle" : "edit-4-fill";
    const toggle = `<button class="btn btn-ghost btn-square btn-sm bg-base-100" type="button" aria-label="${escapeText(toggleLabel)}" data-fishy-tooltip="${escapeText(toggleLabel)}" data-fishy-tooltip-mode="anchor-only" aria-expanded="${state.open}" aria-controls="${panelId}" data-action="toggle-catalog-item-facts" data-item-id="${state.itemId}">${spriteIcon(toggleIcon)}</button>`;
    if (!state.open) {
        return Object.freeze({ toggle, panel: "" });
    }

    const priceValue = state.price.state === "known" ? state.price.value : "";
    const quantity = state.quantityDefault.state === "known"
        ? state.quantityDefault
        : { minimum: "", maximum: "" };
    const priceInputId = `calculator-catalog-item-price-${state.itemId}`;
    const minimumInputId = `calculator-catalog-item-quantity-minimum-${state.itemId}`;
    const maximumInputId = `calculator-catalog-item-quantity-maximum-${state.itemId}`;
    const priceRevert = itemFactRevertButton(
        state.priceUserOperationId,
        t("calculator.data.item_facts.revert_price", "Revert price"),
    );
    const quantityDefaultRevert = itemFactRevertButton(
        state.quantityDefaultUserOperationId,
        t("calculator.data.item_facts.revert_quantity_default", "Revert default"),
    );

    const panel = `<form id="${panelId}" class="col-span-2 mx-1 mb-2" data-catalog-item-facts-form data-item-id="${state.itemId}">`
        + `<fieldset class="fieldset rounded-box border border-base-300 bg-base-200 p-3"${modelLoading ? " disabled" : ""}>`
        + `<legend class="fieldset-legend">${escapeText(t("calculator.data.item_facts.title", "Item facts"))}</legend>`
        + `<p class="label whitespace-normal">${escapeText(t("calculator.data.item_facts.description", "These values apply to this item globally."))}</p>`
        + '<div class="grid gap-3 sm:grid-cols-2">'
        + '<div class="grid content-start gap-2">'
        + `<label class="label" for="${priceInputId}">${escapeText(t("calculator.data.field.price_silver", "Price (silver)"))}</label>`
        + `<label class="input input-sm w-full">${spriteIcon("coin-2-fill", "size-4 opacity-60")}<input id="${priceInputId}" class="grow min-w-0" type="number" min="0" step="any" inputmode="decimal" value="${escapeText(priceValue)}" placeholder="${escapeText(t("calculator.data.item_facts.price_unknown", "Unknown"))}" data-catalog-item-price></label>`
        + (priceRevert
            ? `<div class="flex flex-wrap justify-end gap-1">${priceRevert}</div>`
            : "")
        + '</div>'
        + '<div class="grid content-start gap-2">'
        + `<span class="label">${escapeText(t("calculator.data.item_facts.quantity_default", "Default quantity range"))}</span>`
        + '<div class="grid grid-cols-2 gap-2">'
        + `<div><label class="label" for="${minimumInputId}">${escapeText(t("calculator.data.quantity.minimum", "Minimum"))}</label><input id="${minimumInputId}" class="input input-sm w-full" type="number" min="0" max="${U32_MAX}" step="1" inputmode="numeric" value="${escapeText(quantity.minimum)}" data-catalog-item-quantity-minimum></div>`
        + `<div><label class="label" for="${maximumInputId}">${escapeText(t("calculator.data.quantity.maximum", "Maximum"))}</label><input id="${maximumInputId}" class="input input-sm w-full" type="number" min="0" max="${U32_MAX}" step="1" inputmode="numeric" value="${escapeText(quantity.maximum)}" data-catalog-item-quantity-maximum></div>`
        + '</div>'
        + (quantityDefaultRevert
            ? `<div class="flex flex-wrap justify-end gap-1">${quantityDefaultRevert}</div>`
            : "")
        + '</div></div></fieldset></form>';
    return Object.freeze({ toggle, panel });
}

function formItemId(form) {
    const value = Number(form?.getAttribute?.("data-item-id"));
    if (!Number.isInteger(value) || value < 1 || value > U32_MAX) {
        throw new TypeError(`Item ID must be an integer from 1 through ${U32_MAX}`);
    }
    return value;
}

function formValue(form, selector) {
    return String(form?.querySelector?.(selector)?.value ?? "").trim();
}

export function buildCalculatorCatalogItemPriceDraft(form) {
    const rawPrice = formValue(form, "[data-catalog-item-price]");
    const price = NON_NEGATIVE_DECIMAL_INPUT.test(rawPrice)
        ? Number(rawPrice)
        : Number.NaN;
    return buildSetItemPriceDraft({
        itemId: formItemId(form),
        price: knownPrice(price),
    });
}

export function calculatorCatalogItemPriceFormHasValidInput(form) {
    try {
        buildCalculatorCatalogItemPriceDraft(form);
        return true;
    } catch {
        return false;
    }
}

export function buildCalculatorCatalogItemQuantityDefaultDraft(form) {
    const bound = (selector, label) => {
        const rawValue = formValue(form, selector);
        const value = /^\d+$/u.test(rawValue) ? Number(rawValue) : Number.NaN;
        if (!Number.isInteger(value) || value < 0 || value > U32_MAX) {
            throw new TypeError(
                `${label} quantity must be an integer from 0 through ${U32_MAX}`,
            );
        }
        return value;
    };
    const minimum = bound(
        "[data-catalog-item-quantity-minimum]",
        "Minimum",
    );
    const maximum = bound(
        "[data-catalog-item-quantity-maximum]",
        "Maximum",
    );
    if (maximum < minimum) {
        throw new TypeError("Maximum quantity must be at least the minimum quantity");
    }
    return buildSetItemQuantityDefaultDraft({
        itemId: formItemId(form),
        quantity: { state: "known", minimum, maximum },
    });
}

export function calculatorCatalogItemQuantityDefaultFormHasValidInput(form) {
    try {
        buildCalculatorCatalogItemQuantityDefaultDraft(form);
        return true;
    } catch {
        return false;
    }
}
